/**
 * Pinned household notes: short messages any peon can post to the board,
 * gone 24 hours after posting. See docs/notes.md.
 *
 * Pure, like shared/bounty.ts, so the expiry math and the preview cut run
 * identically on the server and in the browser. The only imports are the
 * household day's calendar helpers, for hearts' gold.
 */
import { localDay } from "./flash.ts";
import { zonedInstant } from "./weekly.ts";

/** Longest a note's body may be, counted in code points. */
export const NOTE_MAX_CHARS = 250;

/** How long a note stays pinned before it expires. */
export const NOTE_TTL_MS = 24 * 60 * 60 * 1000;

/** Longest a collapsed note preview may be, counted in code points. */
export const NOTE_PREVIEW_CHARS = 90;

/** `expires_at` for a note created at `createdAtIso`. */
export function noteExpiresAt(createdAtIso: string): string {
  return new Date(new Date(createdAtIso).getTime() + NOTE_TTL_MS).toISOString();
}

/** A note is active up to, but not including, its expiry instant. */
export function isNoteActive(note: { expires_at: string }, now: Date): boolean {
  return new Date(note.expires_at).getTime() > now.getTime();
}

/**
 * The note card's "expires in 23 h" / "expires in 42 min" / "expires in under a minute". Whole
 * units left, rounded down, never more than the note's whole life (a phone whose clock runs a
 * little behind the server's would otherwise read 25 h on a fresh note); minutes under an hour
 * (owner, 2026-09-30).
 */
export function noteExpiresInLabel(expiresAtIso: string, now: number): string {
  const remaining = Math.min(NOTE_TTL_MS, Math.max(0, new Date(expiresAtIso).getTime() - now));
  const minute = 60_000;
  const hour = 60 * minute;
  if (remaining >= hour) return `expires in ${Math.floor(remaining / hour)} h`;
  if (remaining >= minute) return `expires in ${Math.floor(remaining / minute)} min`;
  return "expires in under a minute";
}

/**
 * Cuts `body` to at most NOTE_PREVIEW_CHARS code points for the collapsed
 * card, breaking at the last space before the limit so words stay whole.
 */
export function notePreview(body: string): { preview: string; truncated: boolean } {
  return cutAt(body, NOTE_PREVIEW_CHARS);
}

function cutAt(body: string, limit: number): { preview: string; truncated: boolean } {
  const chars = Array.from(body);
  if (chars.length <= limit) return { preview: body, truncated: false };
  const cut = chars.slice(0, limit);
  const lastSpace = cut.lastIndexOf(" ");
  const trimmed = (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).join("").trimEnd();
  return { preview: `${trimmed}…`, truncated: true };
}

// ---------- the notice board (docs/notes.md "How notes show") ----------
//
// Owner, 2026-10-05: "Pinned notes should also actually look like pinned notes on a notice board,
// clicking on them will expand them. Think animal crossing note board." Each note is a paper slip
// pinned to cork, a little crooked, two across; a tap opens it to the whole note.

/** Longest a slip's text may be, counted in code points, before it ends in an ellipsis. */
export const NOTE_SLIP_CHARS = 60;

/**
 * What a collapsed slip shows of the note: its first line, cut to NOTE_SLIP_CHARS code points at the
 * last space before the limit, with an ellipsis when anything was left out (a second line counts).
 */
export function noteSlipText(body: string): string {
  const firstLine = body.trim().split(/\r?\n/)[0]!.trim();
  const { preview, truncated } = cutAt(firstLine, NOTE_SLIP_CHARS);
  const more = !truncated && firstLine.length < body.trim().length;
  return more ? `${preview}…` : preview;
}

/** The slips' tilts, in degrees: two to the left, two to the right, none of them straight. */
export const NOTE_TILTS: readonly number[] = [-3, 2, -1.5, 2.5];

/**
 * A slip's tilt in degrees, chosen from the note's id (FNV-1a), so a note leans the same way on every
 * render and on both clients. An expanded or ringing slip is drawn straight instead.
 */
export function noteTilt(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return NOTE_TILTS[h % NOTE_TILTS.length]!;
}

/**
 * The author's stamp on a closed slip, in place of their name (owner, 2026-10-05: "Rather than the
 * peons name on the minimized notes, it should just be a little stamp of their peon"): their peon
 * drawn as small as the gallery tile's (shared/weekly.ts GALLERY_PEON, 46x42 art pixels at two
 * thirds of a point, about 31x28), the mascot in their shirt with their hair, beard and extras, in
 * the peon stamp's ink frame (shared/peon-stamp.ts PEON_STAMP). It sits in the paper's top-left
 * corner, `top` points down (the closed paper's top padding, beside the pin), and the first line
 * follows `gap` under it. Named for a screen reader by the author's name.
 */
export const NOTE_STAMP = { top: 6, gap: 2 } as const;

/**
 * The dog-ear (owner, 2026-10-20: "There needs to be some indication that notes can be expanded
 * visually"): every closed slip that opens has its bottom-right corner folded over, `size` points a
 * side, one art pixel a point like the slip's ink edge. The corner past the crease is gone (the cork
 * and the slip's step shadow show there), and the flap lies on the front: the paper's underside, a
 * shade darker, in the ink edge. The open slip, the add slip and a ringing slip have none.
 */
export const DOG_EAR = { size: 12, underside: "#e0cfa8" } as const;

/**
 * The cut corner's outline, as points measured in from the paper's bottom-right corner ([left of the
 * right edge, up from the bottom edge]), from the right edge round to the bottom edge. The crease
 * steps one pixel at a time, so the edge stays crisp: a cell (i across, j down from the corner
 * square's top-left) is paper while i + j < size.
 */
export function dogEarOutline(size: number = DOG_EAR.size): Array<[number, number]> {
  const points: Array<[number, number]> = [[0, size - 1]];
  for (let k = 1; k < size; k++) points.push([k, size - k], [k, size - k - 1]);
  return points;
}

/**
 * The flap drawn in the corner square (size by size, from its top-left): rectangles of ink (the two
 * folded edges and the crease) and of the underside between them.
 */
export function dogEarFlap(size: number = DOG_EAR.size): { ink: Array<[number, number, number, number]>; underside: Array<[number, number, number, number]> } {
  const ink: Array<[number, number, number, number]> = [
    [0, 0, size, 1],
    [0, 1, 1, size - 1],
  ];
  for (let j = 1; j < size - 1; j++) ink.push([size - 1 - j, j, 1, 1]);
  const underside: Array<[number, number, number, number]> = [];
  for (let j = 1; j < size - 2; j++) underside.push([1, j, size - 2 - j, 1]);
  return { ink, underside };
}

/** What a screen reader hears after a closed slip's name: a tap opens it. */
export const NOTE_SLIP_HINT = "Opens the note";

// ---------- photo notes (docs/notes.md "Photo notes", flag photoNotesUI) ----------
//
// Owner, 2026-10-20: "Allow adding photos to the board." A note may be a photo with an optional
// caption: the photo is an id in the household's photo store (functions/api/photos, the same upload
// a task's before photo takes), the caption is the note's body (its limits, its tags). On the cork it
// is a little instant photo pinned like any slip: the photo in a border of the notes' parchment, the bottom
// margin thicker, the author's stamp and the caption written there.

/**
 * The instant photo's measures, the same on both clients: a real Polaroid's (owner, 2026-10-23:
 * "they should also have a proper poloroid look to them regarding the frame"). Its paper is the
 * notes' own parchment and its dog-ear the parchment's underside (DOG_EAR), as any slip's (owner,
 * 2026-10-20: "The background color of the new notes doesn't match the old"). `border` is the frame
 * at the photo's top and sides as a share of the paper's width, `strip` the bottom strip's depth in
 * borders, which holds the author's stamp and the caption beside it (`captionLines` lines at most,
 * then an ellipsis), caption or not. The closed photo is square (cropped to fill); the open one is
 * the whole photo, no taller than `openMaxHeight`, in the same frame.
 */
export const NOTE_PHOTO = { border: 0.07, strip: 3.5, captionLines: 2, openMaxHeight: 420 } as const;

/** The Polaroid frame for a paper `width` points wide: the border round the photo's top and sides and the bottom strip's depth, whole points. */
export function polaroidFrame(width: number): { border: number; strip: number } {
  const border = Math.max(0, Math.round(width * NOTE_PHOTO.border));
  return { border, strip: Math.round(border * NOTE_PHOTO.strip) };
}

/**
 * The photo sits in the frame, not on it (owner, 2026-10-23: "The photos need to look slightly inset,
 * like a Polaroid. It should look like Polaroid or instant film. It just looks like the photo is
 * sitting on top of the note frame."): the frame's inner edge casts a hard shadow onto the photo along
 * its top and left, `shadow` of dark ink with `shade` of a lighter one under it, and a `lip` of paper
 * lighter than the parchment along its bottom and right, as if the frame's edge overlapped the photo;
 * outside the photo, a `rim` of darker parchment along the window's top and left, where the paper
 * turns down into it. Hard pixels, no blur, in points (CSS px); drawn over the photo and never taking a touch, so the
 * photo's own tap and long press still reach it. Both clients draw these numbers and inks.
 */
export const POLAROID_INSET = { shadow: 2, shade: 1, lip: 1, shadowInk: "rgba(42, 24, 8, 0.45)", shadeInk: "rgba(42, 24, 8, 0.2)", lipPaper: "#fffaeb", rim: 1, rimPaper: "#dcc79f" } as const;

/**
 * The inset's bands for a photo `w` by `h` points (POLAROID_INSET): rectangles in points from the
 * photo's top-left corner, each with its ink, none overlapping; the last two, the rim, lie just
 * outside the photo (negative left and top), so the photo's box must not clip them.
 */
export function polaroidInsetBands(w: number, h: number): { left: number; top: number; width: number; height: number; color: string }[] {
  const { shadow: a, shade: b, lip: l, rim: r, shadowInk, shadeInk, lipPaper, rimPaper } = POLAROID_INSET;
  if (!(w > a + b + l) || !(h > a + b + l)) return [];
  return [
    { left: 0, top: 0, width: w, height: a, color: shadowInk },
    { left: 0, top: a, width: a, height: h - a, color: shadowInk },
    { left: a, top: a, width: w - a - l, height: b, color: shadeInk },
    { left: a, top: a + b, width: b, height: h - a - b - l, color: shadeInk },
    { left: a, top: h - l, width: w - a, height: l, color: lipPaper },
    { left: w - l, top: a, width: l, height: h - a - l, color: lipPaper },
    { left: -r, top: -r, width: w + r, height: r, color: rimPaper },
    { left: -r, top: 0, width: r, height: h, color: rimPaper },
  ];
}

/** A long press on a note's photo on the phone saves it (owner, 2026-10-23: "Long pressing a photo allows you to save it natively"). */
export const NOTE_PHOTO_SAVED = "Saved to your photos.";
/** The same for a screen reader, among the slip's actions. */
export const NOTE_SAVE_PHOTO = "Save photo";

// ---------- the board's masonry ----------
//
// Owner, 2026-10-20, on an iPhone screenshot of a short speedrun slip beside a tall photo note, a cork
// hole under the short one and the add slip on a row of its own below: "Layout here looks kinda bad".
// The slips no longer sit in rows. In the board's order (the speedrun note, the notes, the add slip
// last) each goes to the foot of whichever column is shorter at that moment, ties to the left, so the
// order still reads left to right, top to bottom, and the add slip fills the hole. An open slip still
// takes the board's whole width where it falls: it starts under the taller column, and the slips after
// it start level again under it. Both clients place by measured heights (never percentages on the app).

/** The cork between slips: 16 down a column (the pins stand proud of each slip), 10 across. */
export const BOARD_GAP = { row: 16, column: 10 } as const;

/** Where one slip goes: its column (0 at the left), its top in points from the slips' top, and whether it spans every column (the open slip). */
export interface SlipPlace {
  col: number;
  top: number;
  full: boolean;
}

/**
 * The board's masonry: `heights` are the slips' measured heights in the board's order, `cols` how
 * many columns (2 on a phone), `open` the index of the open slip (-1 for none). Each slip goes under
 * the shorter column, ties to the left; the open one spans them all under the tallest. `height` is the
 * whole stack's height.
 */
export function boardMasonry(heights: readonly number[], cols: number, open = -1, gap: number = BOARD_GAP.row): { places: SlipPlace[]; height: number } {
  const n = Math.max(1, Math.floor(cols));
  const bottoms: number[] = new Array(n).fill(0);
  const places: SlipPlace[] = [];
  heights.forEach((h, i) => {
    if (i === open) {
      const top = Math.max(...bottoms);
      places.push({ col: 0, top, full: true });
      bottoms.fill(top + h + gap);
      return;
    }
    let c = 0;
    for (let k = 1; k < n; k++) if (bottoms[k]! < bottoms[c]!) c = k;
    places.push({ col: c, top: bottoms[c]!, full: false });
    bottoms[c] = bottoms[c]! + h + gap;
  });
  return { places, height: Math.max(0, Math.max(...bottoms) - gap) };
}

/**
 * The masonry cut into runs for the app, which stacks flex columns rather than placing by points:
 * each run is the slips between open ones dealt into `cols` columns (indexes into the board's order),
 * or the open slip alone.
 */
export type BoardRun = { kind: "cols"; cols: number[][] } | { kind: "open"; index: number };
export function boardRuns(heights: readonly number[], cols: number, open = -1): BoardRun[] {
  const { places } = boardMasonry(heights, cols, open);
  const n = Math.max(1, Math.floor(cols));
  const runs: BoardRun[] = [];
  let run: number[][] | null = null;
  places.forEach((p, i) => {
    if (p.full) {
      runs.push({ kind: "open", index: i });
      run = null;
      return;
    }
    if (!run) {
      run = Array.from({ length: n }, () => []);
      runs.push({ kind: "cols", cols: run });
    }
    run[p.col]!.push(i);
  });
  return runs;
}

/** Whether a note is a photo note. */
export function noteHasPhoto(note: { photo_id?: string | null }): boolean {
  return typeof note.photo_id === "string" && note.photo_id.length > 0;
}

/** A photo id as the photo store makes them (server/http.ts newId, a UUID). */
export function isPhotoId(id: unknown): id is string {
  return typeof id === "string" && /^[0-9a-f-]{36}$/.test(id);
}

/** The composer's button, the caption's placeholder once a photo is on, and the button that takes it off again. */
export const NOTE_ADD_PHOTO = "Add a photo";
export const NOTE_CAPTION_PLACEHOLDER = "Add a caption";
export const NOTE_REMOVE_PHOTO = "Remove photo";

/** The photo's name for a screen reader, and its alt text: "Photo from Hue". */
export function notePhotoAlt(author: string): string {
  return `Photo from ${author}`;
}

/**
 * A closed slip's name for a screen reader, before any hearts: "Note from Hue: Pie in the oven", a
 * photo note "Photo from Hue: At the lake", or "Photo from Hue" with no caption.
 */
export function noteSlipLabel(note: { created_by: string; body: string; photo_id?: string | null }): string {
  const text = noteSlipText(note.body);
  if (noteHasPhoto(note)) return text ? `${notePhotoAlt(note.created_by)}: ${text}` : notePhotoAlt(note.created_by);
  return `Note from ${note.created_by}: ${text}`;
}

/** The "pinned" push's title (server/push.ts noteMessage): "Hue pinned a note", or "Hue pinned a photo". */
export function notePinnedTitle(author: string, photo: boolean): string {
  return `${author} pinned a ${photo ? "photo" : "note"}`;
}

/** The pinned photo's push body when it has no caption: a few words, no numbers. */
export const NOTE_PHOTO_PUSH_BODY = "Up on the notice board.";

// ---------- timed notes (docs/timed-notes.md) ----------
//
// A timed note is a pinned note with a time and a list of people: "Pie ready" rings in 40
// minutes for Hue and Jeff. The server fires it (server/timers.ts) and the clients show the
// countdown and ring when it is for them. Everything here is pure so both sides agree.

/**
 * The composer's "Alarm" choices, in minutes. A dropdown, not chips (owner, 2026-10-04: "I hate
 * the timer selector, it should be a dropdown or native"), so the list can be long enough for a
 * kitchen and a long afternoon alike.
 */
export const TIMER_PRESETS_MIN: readonly number[] = [0, 1, 2, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720];

/** A delay of 0: the note rings for its people the moment it is pinned (owner, 2026-10-04: "There's no way to make a note send a notif instantly?"). */
export const TIMER_NOW_MS = 0;

/** The shortest delay a timer may have: the picker turns in seconds, like the phone's own timer. */
export const TIMER_MIN_MS = 1000;

/** A timer never outlives its note, so it rings within the note's 24 hours. */
export const TIMER_MAX_MS = NOTE_TTL_MS;

/**
 * How long a note rings (owner, 2026-10-04: "It should ring for 2 mins or until someone clicks on
 * the notif or acknowledges it in the app"): the server re-pushes it every RING_REPEAT_MS inside
 * this window (a notification's sound cannot outlast 30 s, so "just send another after 30s if
 * still ringing"), and an open client loops the chime for it, until anyone acknowledges it.
 */
export const RING_WINDOW_MS = 2 * 60_000;

/** How long an open client loops the alarm on its own: the whole ring, unless acknowledged. */
export const RING_LOOP_MS = RING_WINDOW_MS;

/** The gap between the server's pushes of one ring. */
export const RING_REPEAT_MS = 30_000;

/** The most pushes one ring sends (the first included): the window over the gap. */
export const RING_MAX_PUSHES = RING_WINDOW_MS / RING_REPEAT_MS;

/** Most people a timer may name; past that it is for everyone anyway. */
export const TIMER_MAX_RECIPIENTS = 20;

/** A duration as the three wheels hold it (hours 0 to 23, minutes and seconds 0 to 59). */
export interface TimerParts {
  hours: number;
  minutes: number;
  seconds: number;
}

/** The wheels' reading of a delay in ms; whole seconds, capped at a day less a second. */
export function timerParts(ms: number): TimerParts {
  const total = Math.min(Math.max(0, Math.floor(ms / 1000)), 24 * 3600 - 1);
  return { hours: Math.floor(total / 3600), minutes: Math.floor((total % 3600) / 60), seconds: total % 60 };
}

/** The delay in ms the wheels mean; 0 means no ring. */
export function timerMs(parts: TimerParts): number {
  return (parts.hours * 3600 + parts.minutes * 60 + parts.seconds) * 1000;
}

/**
 * The collapsed row's reading of the wheels: "off" at zero, else the non-zero parts in the
 * phone's own words ("20 min 7 sec", "1 hour", "2 hours 30 min", "45 sec").
 */
export function timerPartsLabel(parts: TimerParts): string {
  const out: string[] = [];
  if (parts.hours) out.push(`${parts.hours} ${parts.hours === 1 ? "hour" : "hours"}`);
  if (parts.minutes) out.push(`${parts.minutes} min`);
  if (parts.seconds) out.push(`${parts.seconds} sec`);
  return out.length ? out.join(" ") : "off";
}

/** The choices' labels: "Now", "5 min", "45 min", "1 h", "1 h 30 min", "12 h". */
export function timerPresetLabel(minutes: number): string {
  if (minutes === 0) return "Now";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** A delay the server accepts: now (0), or a whole number of milliseconds from one second to one day. */
export function isValidTimerDelay(ms: unknown): ms is number {
  if (ms === TIMER_NOW_MS) return true;
  return typeof ms === "number" && Number.isInteger(ms) && ms >= TIMER_MIN_MS && ms <= TIMER_MAX_MS;
}

/** `fire_at` for a timer set when the note was created. */
export function noteFireAt(createdAtIso: string, delayMs: number): string {
  return new Date(new Date(createdAtIso).getTime() + delayMs).toISOString();
}

/** Trim and collapse inner spaces, as shared/peons.ts normalizeName does (kept local so this file stays dependency-free). */
function cleanName(name: string): string {
  return name.trim().replace(/\s+/g, " ");
}

/** Two peon names are the same person when they match after cleaning, ignoring case. */
export function sameName(a: string, b: string): boolean {
  return cleanName(a).toLowerCase() === cleanName(b).toLowerCase();
}

/**
 * The stored recipients for what the composer picked: "everyone" (or an empty pick) is null,
 * else the cleaned names, unique ignoring case, in the order first picked, blanks dropped.
 */
export function recipientsFor(picked: ReadonlyArray<string> | "everyone"): string[] | null {
  if (picked === "everyone") return null;
  const out: string[] = [];
  for (const raw of picked) {
    const name = cleanName(raw);
    if (!name || out.some((n) => sameName(n, name))) continue;
    out.push(name);
  }
  return out.length ? out : null;
}

/** Whether a timed note is for this person: null is everyone. */
export function isRecipient(recipients: ReadonlyArray<string> | null, me: string): boolean {
  return recipients === null || recipients.some((n) => sameName(n, me));
}

/**
 * The card's people line: "for everyone", "for you", "for you and Hue", "for Hue and Jeff",
 * "for Hue, Jeff and 2 more". At most two names; the reader is always "you" and comes first.
 */
export function noteForLabel(recipients: ReadonlyArray<string> | null, me: string): string {
  if (recipients === null) return "for everyone";
  const others = recipients.filter((n) => !sameName(n, me));
  const names = others.length < recipients.length ? ["you", ...others] : [...others];
  if (names.length === 0) return "for everyone";
  if (names.length === 1) return `for ${names[0]}`;
  if (names.length === 2) return `for ${names[0]} and ${names[1]}`;
  return `for ${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

/**
 * The timer's state line. Before the time: "rings in 2 h", "rings in 38 min", "rings in under a
 * minute" (whole units, rounded down, like noteExpiresInLabel). Once the server has fired it
 * (`fired_at`), or the time has passed by this clock: "ringing" within RING_WINDOW_MS, unless
 * `over` says the ring is done here (acknowledged, or not for this reader), then "rang just now"
 * under a minute, "rang 12 min ago", "rang 3 h ago". The server's mark wins over the clock
 * (owner, 2026-10-04: "The timer says rings in under a minute still after it already rang"): a
 * phone a little behind the server, or a board tick up to a minute stale, must never count down
 * a note that has rung. The "ago" side is measured from the time, never negative.
 */
export function noteRingsLabel(fireAtIso: string, now: number, over = false, firedAtIso: string | null = null): string {
  const minute = 60_000;
  const hour = 60 * minute;
  const delta = new Date(fireAtIso).getTime() - now;
  if (delta > 0 && !firedAtIso) {
    const remaining = Math.min(TIMER_MAX_MS, delta);
    if (remaining >= hour) return `rings in ${Math.floor(remaining / hour)} h`;
    if (remaining >= minute) return `rings in ${Math.floor(remaining / minute)} min`;
    return "rings in under a minute";
  }
  const ago = Math.max(0, -delta);
  if (ago < RING_WINDOW_MS && !over) return "ringing";
  if (ago >= hour) return `rang ${Math.floor(ago / hour)} h ago`;
  if (ago >= minute) return `rang ${Math.floor(ago / minute)} min ago`;
  return "rang just now";
}

/**
 * Whether an open client should ring this note now: the server has fired it, nobody has
 * acknowledged it, it is within the ring window, and it is for `me`. The server's mark is
 * required here (not the clock alone) so a client never rings before the push goes out. An
 * acknowledgement is household-wide (anyone's tap on the notification or Stop in the app ends
 * the ring for everyone); a per-device key only covers the moment before the server confirms it.
 */
/** The card's word for who ended the ring: "Hue got it", "you got it", "" before anyone has. */
export function noteAckLabel(note: { acknowledged_by: string | null; acknowledged_at: string | null }, me: string): string {
  if (!note.acknowledged_at) return "";
  if (!note.acknowledged_by) return "got it";
  return sameName(note.acknowledged_by, me) ? "you got it" : `${note.acknowledged_by} got it`;
}

export function isRingingFor(note: { fire_at: string | null; fired_at: string | null; acknowledged_at?: string | null; recipients: ReadonlyArray<string> | null }, me: string, now: number): boolean {
  if (!note.fire_at || !note.fired_at || note.acknowledged_at) return false;
  const since = now - new Date(note.fired_at).getTime();
  if (since < 0 || since >= RING_WINDOW_MS) return false;
  return isRecipient(note.recipients, me);
}

// ---------- hearts (docs/notes.md "Hearts") ----------
//
// Owner, 2026-10-12: "I want to be able to heart like pinned notes on the board". A quick, wordless
// "nice", "thanks" or "seen it" on a note, so nobody has to pin another note to answer one. Anyone in
// the household but the note's author may heart it, once; a tap again takes the heart back. The
// hearts go with the note.

/** Whether `me` has hearted the note: names match ignoring case, as everywhere a name is compared. */
export function hasHearted(hearts: ReadonlyArray<string> | null | undefined, me: string): boolean {
  return (hearts ?? []).some((n) => sameName(n, me));
}

/** Whether `me` may heart this note: anyone with a name but its author (the server refuses the author too). */
export function canHeart(note: { created_by: string }, me: string): boolean {
  return cleanName(me).length > 0 && !sameName(note.created_by, me);
}

/** The hearts after `me` taps: their heart added at the end, or taken away. The optimistic answer to a tap. */
export function toggleHeart(hearts: ReadonlyArray<string> | null | undefined, me: string): string[] {
  const cur = [...(hearts ?? [])];
  return hasHearted(cur, me) ? cur.filter((n) => !sameName(n, me)) : [...cur, cleanName(me)];
}

/** The count's words for a screen reader, after the slip's name: "1 heart", "3 hearts". */
export function heartsCountLabel(count: number): string {
  return count === 1 ? "1 heart" : `${count} hearts`;
}

/**
 * Who hearted it, in plain words, on the open slip: "Hue", "Hue and Jeff", "Hue, Jeff and 2 more",
 * in the order they hearted. The reader is "you" and comes first, as the timed note's people line
 * (noteForLabel): "you", "you and Hue". Nobody: "".
 */
export function noteHeartsLabel(hearts: ReadonlyArray<string> | null | undefined, me: string): string {
  const all = hearts ?? [];
  const others = all.filter((n) => !sameName(n, me));
  const names = others.length < all.length ? ["you", ...others] : [...others];
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

/**
 * The heart, drawn in pixels like the claim hand (shared/claims.ts CLAIM_HAND): 7x6 art pixels, the
 * slip's ink outline round a red heart with one light pixel. Empty (`filled` false), only the
 * outline, the paper showing through: the open slip's button before you have hearted it.
 */
export const NOTE_HEART: readonly string[] = [
  ".oo.oo.",
  "owrorro",
  "orrrrro",
  ".orrro.",
  "..oro..",
  "...o...",
];
export const NOTE_HEART_COLOURS: Readonly<Record<string, string>> = { o: "#4a2c12", r: "#c8322a", w: "#f08a6a" };

/** The heart as rects (x, y, colour), one per lit pixel, for an SVG on either client. */
export function noteHeartPixels(filled: boolean): { x: number; y: number; fill: string }[] {
  const out: { x: number; y: number; fill: string }[] = [];
  NOTE_HEART.forEach((row, y) =>
    [...row].forEach((ch, x) => {
      if (!filled && ch !== "o") return;
      const fill = NOTE_HEART_COLOURS[ch];
      if (fill) out.push({ x, y, fill });
    }),
  );
  return out;
}

/**
 * The heart's pop when you add yours: three hard frames, no easing (docs/feel.md), the heart's scale
 * in each, then back to 1. None with reduced motion.
 */
export const HEART_POP_FRAMES: readonly number[] = [1.3, 1.15, 1];
export const HEART_POP_FRAME_MS = 60;

// ---------- gold for a heart (docs/economy.md "Gold for a heart on a note") ----------
//
// Owner, 2026-10-12: "When a note is liked, the user placing the note gets 10 gold, max 50 per day so
// it can't be gamed." Someone else's heart pays the note's author shared/bounty.ts GOLD.noteHeart
// (10) once per note and hearter, ever: taking it back moves no gold and hearting it again pays
// nothing. At most NOTE_HEART_GOLD_DAILY_CAP an author a household day, the day turning at the
// household's midnight (the zone the weekly scroll, the roulette's one a day and the gold rush use).
// Past it the heart still goes in, with no gold.

/** The most gold an author earns from hearts in one household day: five hearts. */
export const NOTE_HEART_GOLD_DAILY_CAP = 50;

/** The purse's words for a heart's gold, in the ledger row's detail. */
export const NOTE_HEART_GOLD_TITLE = "A heart on your note";

/** The first instant of `now`'s household day in `zone`: hearts' gold at or after it is today's. */
export function heartGoldDayStart(now: Date, zone: string): Date {
  const [y, m, d] = localDay(now, zone).split("-").map(Number);
  return zonedInstant(y!, m!, d!, 0, zone);
}

/** The heart gold an author has had today: the sum of their 'heart' rows from the household's midnight up to `now`. */
export function heartGoldPaidToday(rows: ReadonlyArray<{ amount: number; created_at: string }>, now: Date, zone: string): number {
  const from = heartGoldDayStart(now, zone).getTime();
  const to = now.getTime();
  let sum = 0;
  for (const r of rows) {
    const at = Date.parse(r.created_at);
    if (Number.isFinite(at) && at >= from && at <= to) sum += r.amount;
  }
  return sum;
}

/**
 * Whether a heart pays its note's author: never if this hearter's heart on this note has paid before
 * (`paidBefore`, a 'heart' row with this note and hearter, which a taken back heart leaves in place),
 * and only while today's heart gold plus `amount` stays within NOTE_HEART_GOLD_DAILY_CAP. The server
 * guards the same in its insert, so two devices at once cannot pass it.
 */
export function heartGoldDue(o: { paidBefore: boolean; paidToday: number; amount: number }): boolean {
  if (o.paidBefore || o.amount <= 0) return false;
  return Math.max(0, o.paidToday) + o.amount <= NOTE_HEART_GOLD_DAILY_CAP;
}

/**
 * A long press on a closed slip (owner, 2026-10-12: "make long pressing on the notes add a heart as
 * well"): it only ever adds. Null when it does nothing at all (the author's own note, or no name),
 * so the press is an ordinary press; otherwise the hearts after it and whether there is anything to
 * tell the server: already hearted, the heart pops again and nothing is sent or taken back.
 */
export function holdHeart(note: { created_by: string }, hearts: ReadonlyArray<string> | null | undefined, me: string): { hearts: string[]; send: boolean } | null {
  if (!canHeart(note, me)) return null;
  const cur = [...(hearts ?? [])];
  return hasHearted(cur, me) ? { hearts: cur, send: false } : { hearts: toggleHeart(cur, me), send: true };
}
